import { useState } from 'react';
import api from '../../services/api';
import { Send, Loader2, AlertTriangle, CheckCircle, Info, Gift } from 'lucide-react';

/**
 * PublishForm — form to submit a thématique for publication
 * No pricing (admin sets prices after approval)
 * Gift: 12-month free subscription on approval
 */
export default function PublishForm({ thematiqueId, thematiqueLabel, nbSousSujets, nbQuestions, onPublished }) {
    const [title, setTitle] = useState(thematiqueLabel || '');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const MIN_SS = 8;
    const MIN_Q = 200;
    const canPublish = nbSousSujets >= MIN_SS && nbQuestions >= MIN_Q;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const { data } = await api.post('/partner/publish.php', {
                thematique_id: thematiqueId,
                title: title.trim(),
                description: description.trim(),
            });
            if (data.success) {
                setSuccess(true);
                if (onPublished) onPublished();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur de soumission');
        }
        setSubmitting(false);
    };

    if (success) {
        return (
            <div className="publish-form__success">
                <CheckCircle size={32} />
                <h3>Thématique soumise pour review !</h3>
                <p>Notre équipe va examiner votre contenu. Si votre thématique est approuvée, vous recevrez :</p>
                <ul style={{ margin: '8px 0', paddingLeft: 20, fontSize: '0.9em' }}>
                    <li><strong>12 mois offerts</strong> sur votre thématique actuelle — puis accès offert tant qu'un utilisateur est abonné à votre thématique publiée</li>
                    <li><strong>6 mois bonus</strong> pour créer une nouvelle thématique personnalisable</li>
                </ul>
            </div>
        );
    }

    return (
        <div className="publish-form">
            {/* Titre retiré — fourni par BaseModal parent ("Publier « X »") */}

            {/* Gift info */}
            <div className="alert alert-info" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16, fontSize: '0.88rem' }}>
                <Gift size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                    Si votre thématique est approuvée, vous recevez :
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        <li><strong>12 mois offerts</strong> sur votre thématique actuelle — puis accès offert tant qu'un utilisateur est abonné</li>
                        <li><strong>6 mois bonus</strong> pour créer une nouvelle thématique personnalisable</li>
                    </ul>
                    <span style={{ fontSize: '0.9em', opacity: 0.8 }}>Les tarifs de la thématique publiée seront fixés par notre équipe.</span>
                </div>
            </div>

            {/* Content check */}
            <div className={`publish-form__check ${canPublish ? 'publish-form__check--ok' : 'publish-form__check--fail'}`}>
                <div>
                    <span>{canPublish ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}</span>
                    <span>{nbSousSujets} / {MIN_SS} sujets minimum</span>
                </div>
                <div>
                    <span>{nbQuestions >= MIN_Q ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}</span>
                    <span>{nbQuestions} / {MIN_Q} questions minimum</span>
                </div>
            </div>

            {!canPublish && (
                <p className="publish-form__warning">
                    <Info size={14} /> Ajoutez plus de contenu avant de pouvoir publier.
                </p>
            )}

            <form onSubmit={handleSubmit}>
                <div className="publish-form__field">
                    <label>Titre public</label>
                    <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                        placeholder="Titre visible par les utilisateurs" required maxLength={100} />
                </div>

                <div className="publish-form__field">
                    <label>Description (optionnel)</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)}
                        placeholder="Décrivez le contenu de cette thématique..." rows={3} maxLength={500} />
                </div>

                {error && <div className="publish-form__error"><AlertTriangle size={14} /> {error}</div>}

                <button type="submit" className="btn btn-primary" disabled={submitting || !canPublish || !title.trim()}>
                    {submitting ? <><Loader2 size={14} className="spin" /> Soumission...</> : <><Send size={14} /> Soumettre pour review</>}
                </button>

                <p className="publish-form__note">
                    Un snapshot figé de votre contenu sera créé lors de la soumission.
                </p>
            </form>
        </div>
    );
}
